import {
  Button,
  ButtonLink,
  DatePickerFieldController,
  Stack,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import { PasswordTextFieldController } from '@fukuri-admins/ui/common/Form/PasswordTextField/PasswordTextFieldController';
import { getUsersLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

type Props = {
  /** 保存ボタンの非活性フラグ */
  isButtonDisabled: boolean;
  /** フォーム送信ハンドラ */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
  /** 利用開始日フィールドの編集可否フラグ */
  isBillableFromEditable: boolean;
  /**
   * メールアドレスが必須かどうか
   *
   * activation_code フラグが無効、または login_method が email の場合に true になる
   * （logics/resolveFieldRequirements の判定結果）。
   */
  isEmailRequired: boolean;
  /**
   * 電話番号フィールドの表示可否
   *
   * activation_code フラグが有効 かつ login_method が phone_number の場合のみ true になる
   * （logics/resolveFieldRequirements の判定結果）。
   */
  isPhoneNumberFieldVisible: boolean;
  /**
   * 家族招待ユーザーフラグ
   *
   * 【PEN-968 対応】true の場合、利用開始日フィールドを非活性化し、labelType を undefined（任意）にする。
   */
  isInvitee: boolean;
};

/**
 * 利用者編集フォームの Presentation コンポーネント
 *
 * @param isButtonDisabled - 保存ボタンの非活性フラグ
 * @param onSubmit - フォーム送信ハンドラ
 * @param isBillableFromEditable - 利用開始日フィールドの編集可否フラグ
 * @param isEmailRequired - メールアドレスが必須かどうか
 * @param isPhoneNumberFieldVisible - 電話番号フィールドの表示可否
 * @param isInvitee - 家族招待ユーザーフラグ（【PEN-968 対応】）
 */
export const EditUserFormPresentation: React.FC<Props> = ({
  isButtonDisabled,
  onSubmit,
  isBillableFromEditable,
  isEmailRequired,
  isPhoneNumberFieldVisible,
  isInvitee,
}) => (
  <form onSubmit={onSubmit}>
    <Stack direction='column' gap={24} width='466px'>
      <h2 className={styles.formTitle}>利用者の編集</h2>
      <TextInputFieldController
        name='fullName'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: 'フルネーム',
        }}
        textInput={{
          placeholder: '山田',
        }}
      />
      <TextInputFieldController
        name='email'
        fieldWrapperProps={{
          labelType: isEmailRequired ? 'required' : undefined,
          labelText: 'メールアドレス',
        }}
        textInput={{
          type: 'email',
          placeholder: 'xxx@xxxx.xx.xx',
        }}
      />
      {isPhoneNumberFieldVisible && (
        <TextInputFieldController
          name='phoneNumber'
          fieldWrapperProps={{
            labelText: '電話番号',
          }}
          textInput={{
            placeholder: '09012345678',
          }}
        />
      )}
      <TextInputFieldController
        name='externalUserId'
        fieldWrapperProps={{
          labelText: '外部ユーザID',
        }}
        textInput={{
          placeholder: 'xxxx',
        }}
      />
      <DatePickerFieldController
        name='billableFrom'
        fieldWrapperProps={{
          labelText: '利用開始日',
          labelType: isInvitee ? undefined : 'required',
          disabled: isInvitee,
        }}
        datePicker={{ disabled: !isBillableFromEditable || isInvitee }}
      />
      <PasswordTextFieldController
        name='password'
        fieldWrapperProps={{
          labelText: 'パスワード',
          beforeMessage: '変更する場合のみ入力してください。',
        }}
      />
      <PasswordTextFieldController
        name='passwordConfirmation'
        fieldWrapperProps={{
          labelText: 'パスワード確認',
        }}
      />
      <Stack direction='row' gap={16}>
        <ButtonLink to={getUsersLink()} variant='outline' size='md'>
          キャンセル
        </ButtonLink>
        <Button type='submit' size='md' disabled={isButtonDisabled}>
          保存
        </Button>
      </Stack>
    </Stack>
  </form>
);
